import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Cross-tab leader election for the notification sound, so N open tabs ring
 * ONCE for the whole computer.
 *
 * The primitive is the Web Locks API: exactly one tab can hold an exclusive
 * lock at a time, waiting tabs are granted it within milliseconds when the
 * holder releases, and the browser releases it by itself if the holder tab is
 * closed or crashes. No heartbeat, no `localStorage` claim to expire, no
 * stale-leader window to reason about.
 *
 * 🚨 THIS HOOK MUST NEVER LISTEN TO `visibilitychange`, AND MUST NEVER
 * RELEASE THE LOCK BECAUSE THE TAB WENT TO THE BACKGROUND.
 *
 * That single line is what killed the previous leader election (deleted
 * 2026-05-23, `NotificationSoundManager.tsx`): it released on
 * `visibilitychange → hidden` and only re-requested on `visible`, so when
 * EVERY tab was in the background — the operator on another monitor, another
 * app, another browser tab — nobody held the lock and the whole computer went
 * silent. "The notifications tab is open, I am looking at something else, it
 * still has to ring" is the operator's explicit requirement. Leadership here
 * is decided by ELIGIBILITY only; visibility is not an input.
 *
 * Eligibility is the caller's business — today it is "this tab has
 * `/predictions/notifications` open". When it goes false the lock is released
 * and any other eligible tab picks it up immediately.
 *
 * No Web Locks (very old Safari, a locked-down enterprise wrapper): every
 * eligible tab reports itself leader. That degrades to the pre-existing
 * behaviour (each tab rings), never to silence — losing the cross-tab dedupe
 * is an annoyance, losing the naked-leg alarm is not. A lock manager that
 * EXISTS but rejects the request takes the same path: only a genuine
 * `AbortError` (we gave up the queue slot ourselves) means "not the leader".
 *
 * ⚠️ Holding the lock ALSO keeps the leader tab alive in the background — but
 * since 2026-08-28 that is a redundant side effect, not this hook's job.
 * `useTabAliveLock` gives EVERY tab with the feed open its own uniquely-named
 * lock for exactly that purpose, so the liveness below no longer depends on
 * winning this election. The paragraph stays because the mechanism is the
 * same one and the 🚨 under it still governs how this lock is held.
 *
 * Holding the lock is also what keeps the leader tab ALIVE in the background.
 * Chromium's freezing intervention lists "the page is currently holding a Web
 * Lock or an IndexedDB transaction" among the conditions under which a page is
 * NOT frozen — freezing a lock holder would deadlock every tab queued behind
 * it. A frozen tab runs no JavaScript, so it answers no engine.io PING and the
 * gateway drops its socket. The continuously-held lock is therefore not just
 * the dedupe primitive, it is why the one tab that must keep ringing does not
 * get suspended when the operator looks at something else.
 *
 * 🚨 That property depends on the lock being held CONTINUOUSLY. Do not switch
 * to `ifAvailable`, a short-lived lock, or any scheme that takes and drops it
 * around each event: they look equivalent for dedupe and are not. (The
 * liveness half of this warning is now covered by `useTabAliveLock`
 * regardless of who leads; the dedupe half still stands entirely on this
 * lock being held continuously.)
 */

/** Lock name. Shared by every tab of the app on this origin. */
export const SOUND_LEADER_LOCK = 'notification-sound-leader';

/**
 * How long a tab the user has NEVER interacted with waits before contending.
 *
 * Autoplay is unlocked per DOCUMENT, so a tab restored by "continue where you
 * left off" and never clicked may be unable to play at all. Delaying its bid
 * lets any tab the operator HAS touched take the lock first. It is a
 * preference, not an exclusion: if no touched tab wants it, the untouched one
 * still leads after the delay — never silence.
 */
const UNTOUCHED_TAB_LEAD_DELAY_MS = 2_000;

/**
 * How often a LEADER with a reason to stand down checks whether anyone is
 * waiting for the lock.
 *
 * The delay above only wins the startup race. It does nothing for the case
 * that actually bites: both tabs are reloaded after a deploy (Cmd-R does not
 * count as an interaction, so `hasBeenActive` is false in both), both bid when
 * their delay elapses, one wins — and then the operator clicks the OTHER one.
 * Web Locks are FIFO, so the tab that can actually play is queued behind a tab
 * whose autoplay is blocked, forever: no sound anywhere, all session.
 *
 * So such a leader watches the queue and, once someone is waiting, lets go and
 * re-requests from the back. Nobody waiting ⇒ it keeps the lock — preference,
 * never exclusion.
 *
 * ⚠️ It does NOT yield once and stop. That is what the deleted latches did,
 * and having a last yield was the bug: a leader that had spent it held the
 * lock while deaf and silenced the whole computer. Two handicapped tabs now
 * ROTATE the lock, rate-limited by YIELD_COOLDOWN_MS — see that constant for
 * why the swapping is harmless and the terminal state was not.
 */
const UNTOUCHED_LEADER_YIELD_POLL_MS = 1_000;

/**
 * How long the leader's own feed may be down before it steps aside.
 *
 * A leader whose socket is dead hears nothing, and — because it is the only
 * tab allowed to ring — nobody else does either. That failure mode did not
 * exist before the cross-tab election: every tab used to ring, so a deaf one
 * cost nothing. The tabs are not symmetric here, either: they land on
 * different gateway pods through the ALB, so one rollout can kill one tab's
 * socket and leave the rest fine.
 *
 * The threshold is what keeps this from churning the lock on every blip. It
 * stands on the churn alone: an earlier version of this paragraph also priced
 * in "dropping the lock drops the freezing exemption that keeps this tab's
 * socket alive", and since 2026-08-28 that is no longer true —
 * `useTabAliveLock` holds that exemption per tab, independently of who leads.
 * A tab that reconnects inside the window keeps the lock and nothing moves.
 */
const DEAF_LEADER_YIELD_MS = 20_000;

/**
 * Minimum gap between two hand-offs from the same tab.
 *
 * 🚨 THIS REPLACED A PAIR OF LATCHES, and the latches were the bug. Modelling
 * "I already stood aside" as a permanent fact — once per mount, then once per
 * reason, then once per outage — kept producing the same failure in new
 * clothes: a tab that had used up its one hand-off held the lock while deaf,
 * and since it is the only tab allowed to ring, that was silence for the whole
 * computer. The last shape looked airtight and was not: the healthy tab merely
 * navigating off the feed and back handed the lock BACK to the deaf leader,
 * which had already spent its yield for that outage and could never spend
 * another.
 *
 * A cooldown has no terminal state. What the latches were really defending
 * against is two equally-handicapped tabs trading the lock as fast as they can
 * poll; rate-limiting turns that into a slow rotation — someone always holds
 * the lock, so sound never stops and the freezing exemption never lapses —
 * while a tab that has NO reason to stand aside simply never does, so the
 * lock still settles on it.
 *
 * The number is a BLIND WINDOW, so it is kept short. Measured: a deaf leader
 * that is re-granted the lock holds it for exactly this long with a healthy
 * tab waiting, and an alarm arriving in that gap is DROPPED, not delayed —
 * the healthy tab drains its queue and discards it because it is not the
 * leader. The poll is 1 Hz, so ten seconds is still a 10× rate limit on the
 * swapping while costing a third of what thirty did.
 */
const YIELD_COOLDOWN_MS = 10_000;

/**
 * How many times we get back in the queue after losing the lock without
 * having given it up.
 *
 * Losing it involuntarily should be impossible (nothing here steals), so the
 * retry is a safety net — and a net that retries forever is a spin loop if a
 * lock manager resolves or rejects every request immediately. Past the cap we
 * stop asking and declare ourselves leader: ringing in every tab is the
 * degraded mode, ringing in none is not.
 */
const MAX_INVOLUNTARY_RETRIES = 5;

type LockManagerLike = {
  request: (
    name: string,
    options: { mode: 'exclusive'; signal?: AbortSignal },
    callback: () => Promise<void>
  ) => Promise<unknown>;
  /** Optional — absent in older engines, in which case nobody yields. */
  query?: () => Promise<{ pending?: { name?: string }[] }>;
};

/** Read at effect time (not module load) so a test can install/remove it. */
function getLockManager(): LockManagerLike | null {
  if (typeof navigator === 'undefined') return null;
  const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks;
  return typeof locks?.request === 'function' ? locks : null;
}

/**
 * Has the user ever interacted with THIS document? Unsupported ⇒ `true`: an
 * unknown answer must never be the reason a tab declines to lead.
 */
function hasUserTouchedThisTab(): boolean {
  const activation = (
    navigator as Navigator & { userActivation?: { hasBeenActive?: boolean } }
  ).userActivation;
  return typeof activation?.hasBeenActive === 'boolean'
    ? activation.hasBeenActive
    : true;
}

/** A rejection that means "we withdrew from the queue", not "locks are broken". */
function isAbortError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { name?: unknown }).name === 'AbortError'
  );
}

let lockFailureReported = false;
function reportLockManagerFailure(err: unknown): void {
  if (lockFailureReported) return;
  lockFailureReported = true;
  Sentry.captureException(
    err instanceof Error ? err : new Error('sound leader lock request failed'),
    { tags: { area: 'notification-sound' } }
  );
}

/**
 * True while THIS tab is the one allowed to play notification sounds.
 * Always false when `eligible` is false.
 */
export function useSoundLeader(
  eligible: boolean,
  options?: { feedConnected?: boolean }
): boolean {
  const [isLeader, setIsLeader] = useState(false);

  /**
   * When this tab's feed went down, or 0 while it is up. A ref, not a
   * dependency: leadership must NOT be re-elected on every blip — the lock is
   * held continuously on purpose — so the watch below reads this instead.
   */
  // `null`, never 0, and that is not style: `performance.now()` starts AT
  // zero, so a feed that is already down on the first render would stamp 0 and
  // a `0`-as-sentinel check would read it as "up" — deaf forever, never
  // standing down, silence for the whole computer.
  const feedDownSince = useRef<number | null>(null);
  const feedConnected = options?.feedConnected !== false;
  // `performance.now()`, not `Date.now()`: this measures HOW LONG the feed has
  // been down, not when. A wall clock that jumps BACKWARDS — a VM or a laptop
  // resuming before NTP re-syncs, which is exactly when a socket is most
  // likely to be dead — makes the elapsed comparison negative and a deaf
  // leader stops stepping aside for the duration of the skew. Same class of
  // silence as everything else on this path, and a monotonic clock is immune.
  if (feedConnected) feedDownSince.current = null;
  else if (feedDownSince.current === null) {
    feedDownSince.current = performance.now();
  }

  useEffect(() => {
    if (!eligible) {
      setIsLeader(false);
      return;
    }

    const locks = getLockManager();
    if (!locks) {
      setIsLeader(true);
      return;
    }

    // `release` resolves the promise handed back to `request`, which is what
    // actually gives the lock up. While we are inside that callback, this tab
    // IS the leader — for as long as it stays eligible, foreground or not.
    let release: (() => void) | null = null;
    let cancelled = false;
    let controller = new AbortController();
    let retries = 0;
    /** Monotonic stamp of our last hand-off — see YIELD_COOLDOWN_MS. */
    let lastYieldAt: number | null = null;
    let yieldTimer: ReturnType<typeof setInterval> | null = null;

    const stopYieldWatch = (): void => {
      if (yieldTimer) clearInterval(yieldTimer);
      yieldTimer = null;
    };

    /**
     * Why this tab should hand the lock to someone else, or `null` to keep it.
     *
     * Two reasons, and both are about THIS tab being a worse ringer than a tab
     * that is waiting — never about which tab is in front:
     *  - the operator has never touched it, so its autoplay may be locked;
     *  - its own feed has been down long enough that it would not hear the
     *    event anyway.
     */
    const reasonToStepAside = (): 'untouched' | 'deaf' | null => {
      if (
        lastYieldAt !== null &&
        performance.now() - lastYieldAt < YIELD_COOLDOWN_MS
      ) {
        return null;
      }
      if (!hasUserTouchedThisTab()) return 'untouched';
      const downSince = feedDownSince.current;
      if (
        downSince !== null &&
        performance.now() - downSince > DEAF_LEADER_YIELD_MS
      ) {
        return 'deaf';
      }
      return null;
    };

    /**
     * While we lead, watch for a contender we should stand down for.
     *
     * `locks.query()` is only consulted once there IS a reason — on a healthy,
     * touched leader the tick costs one comparison and asks the browser
     * nothing.
     */
    const watchForABetterLeader = (): void => {
      if (typeof locks.query !== 'function') return;
      stopYieldWatch();
      yieldTimer = setInterval(() => {
        if (cancelled) return stopYieldWatch();
        // No reason to stand down — and never a reason to ask the browser.
        if (reasonToStepAside() === null) return;
        void locks
          .query?.()
          .then((state) => {
            // `!release` matters: a query that resolves after we already lost
            // the lock must not burn the cooldown on a hand-off that never
            // happened.
            if (cancelled || !release) return;
            // Re-read after the await: the gesture or the reconnect may have
            // landed while the query was in flight.
            if (reasonToStepAside() === null) return;
            const waiting = (state?.pending ?? []).some(
              (entry) => entry?.name === SOUND_LEADER_LOCK
            );
            if (!waiting) return;
            lastYieldAt = performance.now();
            // Releasing sends us through `.then` → `retry()` → back of the
            // queue, so we still lead if the contender goes away.
            release?.();
          })
          .catch(() => {
            // `query` is a diagnostic; a failure must never cost the lock, and
            // must not cost the WATCH either. Tearing the timer down here left
            // the leader permanently unable to stand aside over one transient
            // rejection — only a fresh grant re-arms it, and a leader that
            // never lets go never gets one. There is nothing to spin on: the
            // polling is a 1 Hz interval, not a promise chain, so a
            // persistently broken `query` costs one rejected promise a second.
          });
      }, UNTOUCHED_LEADER_YIELD_POLL_MS);
    };

    /** Lost the lock while still eligible — get back in line, within reason. */
    const retry = (): void => {
      setIsLeader(false);
      retries += 1;
      if (retries > MAX_INVOLUNTARY_RETRIES) {
        setIsLeader(true);
        return;
      }
      contend();
    };

    const contend = (): void => {
      if (cancelled) return;
      controller = new AbortController();
      void locks
        .request(
          SOUND_LEADER_LOCK,
          { mode: 'exclusive', signal: controller.signal },
          () => {
            if (cancelled) return Promise.resolve();
            // Granted: this run of bad luck is over, so the involuntary-loss
            // budget resets. Without this, a tab that legitimately loses and
            // regains the lock six times in one sitting trips the cap and
            // pins itself "leader" WITHOUT holding the lock — ringing on top
            // of the real leader with no way back.
            retries = 0;
            setIsLeader(true);
            watchForABetterLeader();
            return new Promise<void>((resolve) => {
              release = resolve;
            });
          }
        )
        .then(() => {
          // The lock is gone while we are still eligible. We did not give it
          // up — that path sets `cancelled` first — so something took it from
          // us. Get back in the queue instead of going quiet forever.
          release = null;
          if (cancelled) return;
          retry();
        })
        .catch((err) => {
          release = null;
          // Our own withdrawal always happens with `cancelled` already set,
          // so a rejection while still eligible is never us giving up.
          if (cancelled) return;
          if (isAbortError(err)) {
            // Someone stole the lock. Nothing here steals, so this is the
            // safety net — treat it like any other involuntary loss.
            retry();
            return;
          }
          // The lock manager itself refused (opaque origin, enterprise
          // policy, browser bug). Treat it exactly like "no Web Locks here":
          // ring in every tab rather than in none.
          reportLockManagerFailure(err);
          setIsLeader(true);
        });
    };

    // An untouched tab yields the first couple of seconds to a tab the
    // operator has actually clicked, which is the one whose audio is
    // certainly unlocked. See UNTOUCHED_TAB_LEAD_DELAY_MS.
    let delayTimer: ReturnType<typeof setTimeout> | null = null;
    if (hasUserTouchedThisTab()) contend();
    else delayTimer = setTimeout(contend, UNTOUCHED_TAB_LEAD_DELAY_MS);

    return () => {
      cancelled = true;
      setIsLeader(false);
      if (delayTimer) clearTimeout(delayTimer);
      stopYieldWatch();
      // Granted → resolve the holder promise. Still queued → abort the
      // request so we don't take leadership after becoming ineligible.
      if (release) release();
      controller.abort();
    };
  }, [eligible]);

  return isLeader;
}
